import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Button } from "@/components/ui/button";

const COUNTDOWN_SECONDS = 30;
const CONTACT_EMAIL = "brusnak@transparentimaging.com";

export default function NoRoleScreen() {
  const { logout } = useAuth0();
  const [secondsLeft, setSecondsLeft] = useState(COUNTDOWN_SECONDS);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const secondsRef = useRef(COUNTDOWN_SECONDS);

  const signOut = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = null;
    logout({ logoutParams: { returnTo: window.location.origin } });
  }, [logout]);

  useEffect(() => {
    secondsRef.current = COUNTDOWN_SECONDS;

    intervalRef.current = setInterval(() => {
      const prev = secondsRef.current;
      const nextSeconds = prev <= 1 ? 0 : prev - 1;
      secondsRef.current = nextSeconds;
      setSecondsLeft(nextSeconds);

      if (nextSeconds <= 0) {
        if (intervalRef.current) clearInterval(intervalRef.current);
        intervalRef.current = null;
        signOut();
      }
    }, 1000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [signOut]);

  const progress = secondsLeft / COUNTDOWN_SECONDS;
  const radius = 20;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference * (1 - progress);

  return (
    <div className="relative flex min-h-dvh w-full flex-col items-center justify-center overflow-hidden bg-background px-6">
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.03] dark:opacity-[0.06]"
        style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)`,
          backgroundSize: "32px 32px",
        }}
      />

      <div className="relative z-10 flex w-full max-w-md flex-col items-center gap-8 rounded-2xl border bg-card px-10 py-12 shadow-xl">
        <div className="flex h-16 w-16 items-center justify-center rounded-full border-2 border-muted bg-muted/40">
          <svg
            className="h-7 w-7 text-muted-foreground"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="8" r="4" />
            <path d="M4 20c0-4 3.6-7 8-7" />
            <circle cx="18" cy="18" r="3" />
            <path d="M18 15v3l1.5 1.5" />
          </svg>
        </div>

        <div className="flex flex-col items-center gap-3 text-center">
          <h1 className="text-xl font-semibold tracking-tight text-foreground">
            No role assigned yet
          </h1>
          <p className="text-sm leading-relaxed text-muted-foreground">
            You're logged in, but your account hasn't been assigned a role.
            Please reach out and an administrator will get you set up.
          </p>
        </div>

        <div className="flex w-full flex-col items-center gap-1.5 rounded-lg border border-dashed bg-muted/30 px-5 py-4">
          <span className="text-xs font-medium uppercase tracking-widest text-muted-foreground">
            Contact
          </span>
          <a
            href={`mailto:${CONTACT_EMAIL}`}
            className="text-sm font-medium text-foreground underline-offset-4 hover:underline"
          >
            {CONTACT_EMAIL}
          </a>
        </div>

        <div className="flex w-full flex-col items-center gap-3">
          <Button variant="outline" className="w-full" onClick={signOut}>
            Sign Out
          </Button>

          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <svg
              className="h-6 w-6 -rotate-90"
              viewBox="0 0 48 48"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle
                cx="24"
                cy="24"
                r={radius}
                stroke="currentColor"
                strokeWidth="3"
                className="opacity-15"
              />
              <circle
                cx="24"
                cy="24"
                r={radius}
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeDasharray={circumference}
                strokeDashoffset={strokeDashoffset}
                style={{ transition: "stroke-dashoffset 0.8s linear" }}
              />
            </svg>
            <span>
              Signing out automatically in{" "}
              <span className="tabular-nums font-medium text-foreground">
                {secondsLeft}s
              </span>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
